// AddNewUser.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaPencilAlt } from "react-icons/fa";
import axios from "axios";
import Swal from "sweetalert2";

const AddNewUser = () => {
  // User Details state
  const [formData, setFormData] = useState({
    salutation: "Mr",
    first_name: "",
    middleInitial: "",
    last_name: "",
    externalId: "",
    username: "",
    email: "",
    password: "",
    role: "Dental Lab",
  });

  // Lab Details state
  const [labDetails, setLabDetails] = useState({
    labId: "", // Add labId field
    labName: "",
    address: "",
    address2: "",
    country: "",
    city: "",
    telephone: "",
    utc: "",
    stateProvince: "",
    zipPostal: "",
    email: "",
    subdomain: "",
  });

  // Terms & Conditions state
  const [termsData, setTermsData] = useState({
    receiveInfo: false,
    agreeTerms: false,
    acceptPrivacy: false,
  });

  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentUserLabId, setCurrentUserLabId] = useState("");
  const [isLabDetailsAutoFilled, setIsLabDetailsAutoFilled] = useState(false);
  const [isLoadingLabDetails, setIsLoadingLabDetails] = useState(false);

  const navigate = useNavigate();

  // Get current user's labId from localStorage and fetch lab details if exists
  useEffect(() => {
    const checkUserLabId = () => {
      let labId = localStorage.getItem("userLabId");
      
      if (!labId) {
        const userDataStr = localStorage.getItem("userData");
        if (userDataStr) {
          try {
            const userData = JSON.parse(userDataStr);
            labId = userData.userLabId || userData.userLabId;
            if (labId) {
              localStorage.setItem("userLabId", labId);
            }
          } catch (e) {
            console.error("Error parsing userData:", e);
          }
        }
      }
      
      if (!labId) {
        const labDataStr = localStorage.getItem("labData");
        if (labDataStr) {
          try {
            const labData = JSON.parse(labDataStr);
            labId = labData.id || labData.labId;
            if (labId) {
              localStorage.setItem("userLabId", labId);
            }
          } catch (e) {
            console.error("Error parsing labData:", e);
          }
        }
      }
      
      console.log("Current user labId:", labId);
      setCurrentUserLabId(labId || "");
      
      if (labId) {
        fetchLabDetails(labId);
      } else {
        setIsLoadingLabDetails(false);
      }
    };
    
    checkUserLabId();
  }, []);

  const fetchLabDetails = async (labId) => {
    setIsLoadingLabDetails(true);
    try {
      const token = localStorage.getItem("accessToken") || 
                   localStorage.getItem("token") || 
                   localStorage.getItem("authToken");
      
      if (!token) {
        console.error("No token found for fetching lab details");
        setIsLoadingLabDetails(false);
        return;
      }

      console.log("Fetching lab details for labId:", labId);
      
      const response = await axios.get(
        `https://hms.backend.hiragroupcloud.com/api/auth/lab-details?labId=${encodeURIComponent(labId)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Lab details response:", response.data);
      
      if (response.data.success && response.data.labDetails) {
        console.log("Setting lab details:", response.data.labDetails);
        setLabDetails(response.data.labDetails);
        setIsLabDetailsAutoFilled(true);
      } else {
        console.log("No lab details returned:", response.data.message);
        setIsLabDetailsAutoFilled(false);
        if (formData.role !== 'Admin' && formData.role !== 'admin') {
          setLabDetails({ 
            labId: "", // Reset labId as well
            labName: "", 
            address: "", 
            address2: "", 
            country: "", 
            city: "", 
            telephone: "", 
            utc: "", 
            stateProvince: "", 
            zipPostal: "", 
            email: "", 
            subdomain: "" 
          });
        }
      }
    } catch (error) {
      console.error("Error fetching lab details:", error);
      setIsLabDetailsAutoFilled(false);
      if (formData.role !== 'Admin' && formData.role !== 'admin') {
        setError("Could not load lab details");
      }
    } finally {
      setIsLoadingLabDetails(false);
    }
  };

  const handleUserChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    
    if (id === 'role' && (value === 'Admin' || value === 'admin')) {
      setIsLabDetailsAutoFilled(false);
    } else if (id === 'role' && value !== 'Admin' && value !== 'admin' && currentUserLabId) {
      fetchLabDetails(currentUserLabId);
      setIsLabDetailsAutoFilled(true);
    }
  };

  const handleLabChange = (e) => {
    const { id, value } = e.target;
    setLabDetails((prev) => ({ ...prev, [id]: value }));
  };

  const handleTermsChange = (e) => {
    const { id, checked } = e.target;
    setTermsData((prev) => ({ ...prev, [id]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    if (!termsData.agreeTerms) {
      setError("You must agree to the Terms & Conditions.");
      Swal.fire({ 
        icon: "error", 
        title: "Terms Not Accepted", 
        text: "Please agree to the Terms & Conditions.", 
        confirmButtonColor: "#d33" 
      });
      setIsSubmitting(false);
      return;
    }
    
    if (!termsData.acceptPrivacy) {
      setError("You must accept the Privacy Policy.");
      Swal.fire({ 
        icon: "error", 
        title: "Privacy Policy Not Accepted", 
        text: "Please accept the Privacy Policy.", 
        confirmButtonColor: "#d33" 
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const token = localStorage.getItem("accessToken") || 
                   localStorage.getItem("token") || 
                   localStorage.getItem("authToken");
                   
      if (!token) {
        setError("You need to be logged in to add users");
        Swal.fire({ 
          icon: "error", 
          title: "Authentication Required", 
          text: "You need to be logged in to add users", 
          confirmButtonColor: "#004aad" 
        });
        setIsSubmitting(false);
        return;
      }

      // Make sure labId is included in labDetails
      const payload = {
        ...formData,
        labDetails: {
          ...labDetails,
          labId: labDetails.userLabId || currentUserLabId // Ensure labId is sent
        },
        terms: termsData,
      };

      console.log("Submitting payload with labId:", payload.labDetails.userLabId);

      const response = await axios.post(
        "https://hms.backend.hiragroupcloud.com/api/auth/add",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Response:", response.data);

      Swal.fire({
        icon: "success",
        title: "User added successfully",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });

      // Reset form
      setFormData({
        salutation: "Mr",
        first_name: "",
        middleInitial: "",
        last_name: "",
        externalId: "",
        username: "",
        email: "",
        password: "",
        role: "Dental Lab",
      });
      
      if (!currentUserLabId || formData.role === 'Admin' || formData.role === 'admin') {
        setLabDetails({ 
          labId: "", // Reset labId
          labName: "", 
          address: "", 
          address2: "", 
          country: "", 
          city: "", 
          telephone: "", 
          utc: "", 
          stateProvince: "", 
          zipPostal: "", 
          email: "", 
          subdomain: "" 
        });
        setIsLabDetailsAutoFilled(false);
      } else {
        fetchLabDetails(currentUserLabId);
        setIsLabDetailsAutoFilled(true);
      }
      
      setTermsData({ receiveInfo: false, agreeTerms: false, acceptPrivacy: false });

    } catch (err) {
      console.error("Error submitting form:", err);
      const errorMessage = err.response?.data?.message || err.message || "Failed to add user.";
      setError(errorMessage);
      Swal.fire({ 
        icon: "error", 
        title: "Oops!", 
        text: errorMessage, 
        confirmButtonColor: "#d33" 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLabDetailsEditable = !currentUserLabId || 
                               formData.role === 'Admin' || 
                               formData.role === 'admin';

  return (
    <>
      <div style={{ width: "95%", margin: "0 auto", padding: "0px" }}>
        <div className="row">
          <div className="col-md-10">
            <div className="main-content">
              <h2>Add New User</h2>

              {isLoadingLabDetails && (
                <div
                  style={{
                    color: "#004aad",
                    backgroundColor: "#e6f0ff",
                    padding: "10px",
                    borderRadius: "5px",
                    marginBottom: "15px",
                  }}
                >
                  Loading lab details...
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* ========== USER DETAILS SECTION ========== */}
                <h3 style={{ color: "#1a73e8" }}>User Details</h3>
                <div
                  style={{
                    backgroundColor: "#f4f4f4",
                    padding: "20px",
                    borderTop: "2px solid #004aad",
                  }}
                >
                  {/* Salutation */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="salutation"
                      style={{ marginRight: "10px", marginBottom: "0", width: "100px" }}
                    >
                      Salutation
                    </label>
                    <select
                      id="salutation"
                      className="form-control"
                      style={{ width: "200px", marginLeft: "29px" }}
                      value={formData.salutation}
                      onChange={handleUserChange}
                      disabled={isSubmitting}
                    >
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Ms">Ms</option>
                      <option value="Dr">Dr</option>
                      <option value="Prof">Prof</option>
                    </select>
                  </div>
                  <br />

                  {/* First Name */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="first_name"
                      style={{ marginRight: "10px", marginBottom: "0", width: "100px" }}
                    >
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      className="form-control"
                      style={{ width: "200px", marginLeft: "20px" }}
                      value={formData.first_name}
                      onChange={handleUserChange}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <br />

                  {/* Middle Initial */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="middleInitial"
                      style={{ marginRight: "10px", marginBottom: "0", width: "100px" }}
                    >
                      Middle Initial
                    </label>
                    <input
                      type="text"
                      id="middleInitial"
                      className="form-control"
                      style={{ width: "200px", marginLeft: "10px" }}
                      value={formData.middleInitial}
                      onChange={handleUserChange}
                      maxLength="1"
                      disabled={isSubmitting}
                    />
                  </div>
                  <br />

                  {/* Last Name */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="last_name"
                      style={{ marginRight: "10px", marginBottom: "0", width: "100px" }}
                    >
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      className="form-control"
                      style={{ width: "200px", marginLeft: "20px" }}
                      value={formData.last_name}
                      onChange={handleUserChange}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <br />

                  {/* External ID */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="externalId"
                      style={{ marginRight: "10px", marginBottom: "0", width: "100px" }}
                    >
                      External ID
                    </label>
                    <input
                      type="text"
                      id="externalId"
                      className="form-control"
                      style={{ width: "200px", marginLeft: "18px" }}
                      value={formData.externalId}
                      onChange={handleUserChange}
                      disabled={isSubmitting}
                    />
                  </div>
                  <br />

                  {/* Username */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="username"
                      style={{ marginRight: "10px", marginBottom: "0", width: "100px" }}
                    >
                      Username *
                    </label>
                    <input
                      type="text"
                      id="username"
                      className="form-control"
                      style={{ width: "200px", marginLeft: "20px" }}
                      value={formData.username}
                      onChange={handleUserChange}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <br />

                  {/* Email */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="email"
                      style={{ marginRight: "10px", marginBottom: "0", width: "100px" }}
                    >
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      className="form-control"
                      style={{ width: "200px", marginLeft: "20px" }}
                      value={formData.email}
                      onChange={handleUserChange}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <br />

                  {/* Password */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="password"
                      style={{ marginRight: "10px", marginBottom: "0", width: "100px" }}
                    >
                      Password *
                    </label>
                    <input
                      type="password"
                      id="password"
                      className="form-control"
                      style={{ width: "200px", marginLeft: "20px" }}
                      value={formData.password}
                      onChange={handleUserChange}
                      required
                      minLength="6"
                      disabled={isSubmitting}
                    />
                  </div>
                  <br />

                  {/* Role */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="role"
                      style={{ marginRight: "10px", marginBottom: "0", width: "100px" }}
                    >
                      Role *
                    </label>
                    <select
                      id="role"
                      className="form-control"
                      style={{ width: "200px", marginLeft: "29px" }}
                      value={formData.role}
                      onChange={handleUserChange}
                      disabled={isSubmitting}
                      required
                    >
                      <option value="Dental Lab">Dental Lab</option>
                      <option value="Dental Practice">Dental Practice</option>
                      <option value="Admin">Admin</option>
                      <option value="User">User</option>
                    </select>
                  </div>
                </div>

                {/* ========== LAB DETAILS SECTION ========== */}
                <br />
                <h3 style={{ color: "#1a73e8" }}>Lab Details</h3>
                <div
                  style={{
                    backgroundColor: "#f4f4f4",
                    padding: "20px",
                    borderTop: "2px solid #004aad",
                    opacity: isLabDetailsEditable ? 1 : 0.8,
                  }}
                >
                  {/* Lab ID (Hidden field - just for reference) */}
                  <input type="hidden" id="labId" value={labDetails.labId || currentUserLabId} />
                  
                  {/* Lab Name */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="labName"
                      style={{ marginRight: "10px", marginBottom: "0", width: "140px" }}
                    >
                      Lab Name *
                    </label>
                    <input
                      type="text"
                      id="labName"
                      className="form-control"
                      style={{ width: "300px", marginLeft: "10px" }}
                      value={labDetails.labName}
                      onChange={handleLabChange}
                      required={isLabDetailsEditable}
                      disabled={isSubmitting || !isLabDetailsEditable}
                      readOnly={!isLabDetailsEditable}
                    />
                  </div>
                  <br />

                  {/* Address */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="address"
                      style={{ marginRight: "10px", marginBottom: "0", width: "140px" }}
                    >
                      Address *
                    </label>
                    <input
                      type="text"
                      id="address"
                      className="form-control"
                      style={{ width: "300px", marginLeft: "10px" }}
                      value={labDetails.address}
                      onChange={handleLabChange}
                      required={isLabDetailsEditable}
                      disabled={isSubmitting || !isLabDetailsEditable}
                      readOnly={!isLabDetailsEditable}
                    />
                  </div>
                  <br />

                  {/* Address 2 */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="address2"
                      style={{ marginRight: "10px", marginBottom: "0", width: "140px" }}
                    >
                      Address 2 (Optional)
                    </label>
                    <input
                      type="text"
                      id="address2"
                      className="form-control"
                      style={{ width: "300px", marginLeft: "10px" }}
                      value={labDetails.address2}
                      onChange={handleLabChange}
                      disabled={isSubmitting || !isLabDetailsEditable}
                      readOnly={!isLabDetailsEditable}
                    />
                  </div>
                  <br />

                  {/* Country */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="country"
                      style={{ marginRight: "10px", marginBottom: "0", width: "140px" }}
                    >
                      Country *
                    </label>
                    <input
                      type="text"
                      id="country"
                      className="form-control"
                      style={{ width: "300px", marginLeft: "10px" }}
                      value={labDetails.country}
                      onChange={handleLabChange}
                      required={isLabDetailsEditable}
                      disabled={isSubmitting || !isLabDetailsEditable}
                      readOnly={!isLabDetailsEditable}
                    />
                  </div>
                  <br />

                  {/* City */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="city"
                      style={{ marginRight: "10px", marginBottom: "0", width: "140px" }}
                    >
                      City *
                    </label>
                    <input
                      type="text"
                      id="city"
                      className="form-control"
                      style={{ width: "300px", marginLeft: "10px" }}
                      value={labDetails.city}
                      onChange={handleLabChange}
                      required={isLabDetailsEditable}
                      disabled={isSubmitting || !isLabDetailsEditable}
                      readOnly={!isLabDetailsEditable}
                    />
                  </div>
                  <br />

                  {/* Telephone */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="telephone"
                      style={{ marginRight: "10px", marginBottom: "0", width: "140px" }}
                    >
                      Telephone
                    </label>
                    <input
                      type="tel"
                      id="telephone"
                      className="form-control"
                      style={{ width: "300px", marginLeft: "10px" }}
                      value={labDetails.telephone}
                      onChange={handleLabChange}
                      disabled={isSubmitting || !isLabDetailsEditable}
                      readOnly={!isLabDetailsEditable}
                    />
                  </div>
                  <br />

                  {/* UTC */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="utc"
                      style={{ marginRight: "10px", marginBottom: "0", width: "140px" }}
                    >
                      UTC
                    </label>
                    <input
                      type="text"
                      id="utc"
                      className="form-control"
                      style={{ width: "300px", marginLeft: "10px" }}
                      value={labDetails.utc}
                      onChange={handleLabChange}
                      placeholder="e.g., UTC+5"
                      disabled={isSubmitting || !isLabDetailsEditable}
                      readOnly={!isLabDetailsEditable}
                    />
                  </div>
                  <br />

                  {/* State / Province */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="stateProvince"
                      style={{ marginRight: "10px", marginBottom: "0", width: "140px" }}
                    >
                      State / Province
                    </label>
                    <input
                      type="text"
                      id="stateProvince"
                      className="form-control"
                      style={{ width: "300px", marginLeft: "10px" }}
                      value={labDetails.stateProvince}
                      onChange={handleLabChange}
                      disabled={isSubmitting || !isLabDetailsEditable}
                      readOnly={!isLabDetailsEditable}
                    />
                  </div>
                  <br />

                  {/* Zip / Postal Code */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="zipPostal"
                      style={{ marginRight: "10px", marginBottom: "0", width: "140px" }}
                    >
                      Zip / Postal Code
                    </label>
                    <input
                      type="text"
                      id="zipPostal"
                      className="form-control"
                      style={{ width: "300px", marginLeft: "10px" }}
                      value={labDetails.zipPostal}
                      onChange={handleLabChange}
                      disabled={isSubmitting || !isLabDetailsEditable}
                      readOnly={!isLabDetailsEditable}
                    />
                  </div>
                  <br />

                  {/* Email (Optional - lab contact) */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="email"
                      style={{ marginRight: "10px", marginBottom: "0", width: "140px" }}
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      className="form-control"
                      style={{ width: "300px", marginLeft: "10px" }}
                      value={labDetails.email}
                      onChange={handleLabChange}
                      disabled={isSubmitting || !isLabDetailsEditable}
                      readOnly={!isLabDetailsEditable}
                    />
                  </div>
                  <br />

                  {/* Subdomain */}
                  <div
                    className="form-group"
                    style={{
                      marginBottom: "0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <label
                      htmlFor="subdomain"
                      style={{ marginRight: "10px", marginBottom: "0", width: "140px" }}
                    >
                      Subdomain
                    </label>
                    <input
                      type="text"
                      id="subdomain"
                      className="form-control"
                      style={{ width: "300px", marginLeft: "10px" }}
                      value={labDetails.subdomain}
                      onChange={handleLabChange}
                      disabled={isSubmitting || !isLabDetailsEditable}
                      readOnly={!isLabDetailsEditable}
                    />
                  </div>
                </div>

                {/* ========== TERMS & CONDITIONS SECTION ========== */}
                <br />
                <h3 style={{ color: "#1a73e8" }}>Terms and Conditions</h3>
                <div
                  style={{
                    backgroundColor: "#f4f4f4",
                    padding: "20px",
                    borderTop: "2px solid #004aad",
                  }}
                >
                  <div
                    style={{
                      marginBottom: "15px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="receiveInfo"
                      checked={termsData.receiveInfo}
                      onChange={handleTermsChange}
                      disabled={isSubmitting}
                      style={{ marginRight: "10px" }}
                    />
                    <label htmlFor="receiveInfo" style={{ marginBottom: "0" }}>
                      I'd like to receive information about Labnext Certified Training programs
                    </label>
                  </div>

                  <div
                    style={{
                      marginBottom: "15px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="agreeTerms"
                      checked={termsData.agreeTerms}
                      onChange={handleTermsChange}
                      disabled={isSubmitting}
                      style={{ marginRight: "10px" }}
                    />
                    <label htmlFor="agreeTerms" style={{ marginBottom: "0" }}>
                      I agree to the Terms & Conditions Agreement?
                    </label>
                  </div>

                  <div
                    style={{
                      marginBottom: "15px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="acceptPrivacy"
                      checked={termsData.acceptPrivacy}
                      onChange={handleTermsChange}
                      disabled={isSubmitting}
                      style={{ marginRight: "10px" }}
                    />
                    <label htmlFor="acceptPrivacy" style={{ marginBottom: "0" }}>
                      I accept the Privacy Policy
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <div
                  style={{
                    padding: "10px 0 20px 0",
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                  }}
                >
                  <button
                    type="submit"
                    style={{
                      padding: "8px 10px",
                      backgroundColor: isSubmitting ? "#cccccc" : "#004aad",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                    disabled={isSubmitting}
                  >
                    <FaPencilAlt />
                    {isSubmitting ? "Adding..." : "Add User"}
                  </button>

                  {isSubmitting && (
                    <span style={{ marginLeft: "10px", color: "#666" }}>
                      Adding user...
                    </span>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddNewUser;